import type { Facing } from '../contracts/presentation-snapshot';
import type { LocalAabb } from '../physics/aabb';
import type { TrapSpatialState } from '../physics/spatial-types';
import type { EffectDefinition, GenericTrapDefinition } from './attack';

export const MAX_GAUGE = 100;

export const clampGauge = (value: number): number => {
  if (!Number.isFinite(value)) throw new RangeError('gauge must be finite.');
  return Math.min(MAX_GAUGE, Math.max(0, value));
};

export interface ComboRuntime {
  readonly comboId: string;
  readonly attackerId: string;
  readonly defenderId: string;
  hitCount: number;
  totalDamage: number;
  oncePerComboAppliedEffects: Set<string>;
}

export const createComboRuntime = (comboId: string, attackerId: string, defenderId: string): ComboRuntime => ({
  comboId,
  attackerId,
  defenderId,
  hitCount: 0,
  totalDamage: 0,
  oncePerComboAppliedEffects: new Set(),
});

export const cloneComboRuntime = (combo: Readonly<ComboRuntime>): ComboRuntime => ({
  comboId: combo.comboId,
  attackerId: combo.attackerId,
  defenderId: combo.defenderId,
  hitCount: combo.hitCount,
  totalDamage: combo.totalDamage,
  oncePerComboAppliedEffects: new Set(combo.oncePerComboAppliedEffects),
});

export const makeOncePerComboKey = (comboId: string, effectDefinitionId: string): string =>
  `${comboId}\u0000${effectDefinitionId}`;

export interface CombatEffectRuntime {
  readonly runtimeId: string;
  readonly definitionId: string;
  readonly type: EffectDefinition['type'];
  remainingFrames: number;
  readonly strength: number;
  readonly responseAttackId: string | null;
  readonly appliedLogicalFrame: number;
}

export const cloneCombatEffectRuntime = (effect: Readonly<CombatEffectRuntime>): CombatEffectRuntime => ({ ...effect });

export interface CombatTrapRuntime extends TrapSpatialState {
  readonly definitionId: string;
  readonly attackId: string;
  readonly hitbox: LocalAabb;
  remainingFrames: number;
  hitRecord: Set<string>;
  readonly spawnedLogicalFrame: number;
}

export const createCombatTrapRuntime = (
  runtimeId: string,
  ownerId: string,
  position: { x: number; y: number },
  facing: Facing,
  definition: GenericTrapDefinition,
  logicalFrame: number,
): CombatTrapRuntime => ({
  instanceId: runtimeId,
  ownerId,
  position: { ...position },
  spawnFacing: facing,
  definitionId: definition.id,
  attackId: definition.attackId,
  hitbox: { ...definition.hitbox },
  remainingFrames: definition.lifetimeFrames,
  hitRecord: new Set(),
  spawnedLogicalFrame: logicalFrame,
});

export const cloneCombatTrapRuntime = (trap: Readonly<CombatTrapRuntime>): CombatTrapRuntime => ({
  instanceId: trap.instanceId,
  ownerId: trap.ownerId,
  position: { ...trap.position },
  spawnFacing: trap.spawnFacing,
  definitionId: trap.definitionId,
  attackId: trap.attackId,
  hitbox: { ...trap.hitbox },
  remainingFrames: trap.remainingFrames,
  hitRecord: new Set(trap.hitRecord),
  spawnedLogicalFrame: trap.spawnedLogicalFrame,
});

export type RoundResult = Readonly<{
  winner: 0 | 1 | null;
  reason: 'KO' | 'TIME_UP' | 'DRAW';
}>;

export const resolveRoundResult = (
  hp: readonly [number, number],
  timerFrames: number,
): RoundResult | null => {
  const firstKo = hp[0] <= 0;
  const secondKo = hp[1] <= 0;
  if (firstKo && secondKo) return Object.freeze({ winner: null, reason: 'DRAW' });
  if (firstKo) return Object.freeze({ winner: 1, reason: 'KO' });
  if (secondKo) return Object.freeze({ winner: 0, reason: 'KO' });
  if (timerFrames > 0) return null;
  if (hp[0] === hp[1]) return Object.freeze({ winner: null, reason: 'DRAW' });
  return Object.freeze({ winner: hp[0] > hp[1] ? 0 : 1, reason: 'TIME_UP' });
};
